// Modules.jsx
import React, { useState, useEffect } from 'react';
import { Container, Button, Skeleton } from "@bsf/force-ui";
import { LoaderCircle, SearchIcon } from "lucide-react";

import apiFetch from '@wordpress/api-fetch';
import { __ } from "@wordpress/i18n";
import ModuleItem from '../Dashboard/ModuleItem';

const ElementModules = () => {
    const [allModulesData, setAllModulesData] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loadingActivate, setLoadingActivate] = useState(false);
    const [loadingDeactivate, setLoadingDeactivate] = useState(false);
    const [loading, setLoading] = useState(true);
    const [updateCounter, setUpdateCounter] = useState(0);
    const [showTooltip, setShowTooltip] = useState(true);

    useEffect(() => {
        const fetchModules = () => {
            setLoading(true);
            apiFetch({
                path: 'uavc/v1/modules',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': uavcSettingsData.nonce,
                },
            })
                .then((data) => {
                    if (data && !data.error) {
                        const modulesData = convertToModulesArray(data);
                        setAllModulesData(modulesData);
                    } else {
                        console.error('Error in modules data:', data);
                    }
                    setLoading(false);
                })
                .catch((err) => {
                    console.error('Error fetching modules:', err);
                    setLoading(false);
                });
        };

        fetchModules();
    }, []);

    // Handle search input change
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value.toLowerCase());
    };

    // Filter modules based on search term
    const filteredModules = allModulesData?.filter(module =>
        module.title.toLowerCase().includes(searchTerm)
    );

    const handleActivateAll = () => {
        setLoadingActivate(true);

        const formData = new window.FormData();
        formData.append('action', 'uavc_bulk_activate_modules');
        formData.append('nonce', uavcSettingsData.nonce);

        fetch(uavcSettingsData.ajax_url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin'
        })
        .then(response => response.text())
        .then(text => {
            try {
                const data = JSON.parse(text);
                setLoadingActivate(false);
                if (data.success) {
                    // Update all modules to active state
                    setAllModulesData(prevModules =>
                        prevModules.map(module => ({ ...module, is_active: true }))
                    );
                    // Increment counter to trigger useEffect in ModuleItem
                    setUpdateCounter(prev => prev + 1);
                }
            } catch (e) {
                console.error('Error parsing JSON:', e);
                setLoadingActivate(false);
            }
        })
        .catch((error) => {
            setLoadingActivate(false);
            console.error('Error during AJAX request:', error);
        });
    };

    const handleDeactivateAll = () => {
        setLoadingDeactivate(true);

        const formData = new window.FormData();
        formData.append('action', 'uavc_bulk_deactivate_modules');
        formData.append('nonce', uavcSettingsData.nonce);

        fetch(uavcSettingsData.ajax_url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin'
        })
        .then(response => response.text())
        .then(text => {
            try {
                const data = JSON.parse(text);
                setLoadingDeactivate(false);
                if (data.success) {
                    // Update all modules to inactive state
                    setAllModulesData(prevModules =>
                        prevModules.map(module => ({ ...module, is_active: false }))
                    );
                    // Increment counter to trigger useEffect in ModuleItem
                    setUpdateCounter(prev => prev + 1);
                }
            } catch (e) {
                console.error('Error parsing JSON:', e);
                setLoadingDeactivate(false);
            }
        })
        .catch((error) => {
            setLoadingDeactivate(false);
            console.error('Error during AJAX request:', error);
        });
    };

    function convertToModulesArray(data) {

        const modules = [];

        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                const module = data[key];
                modules.push({
                    id: key, // Using the key as 'widgetTitle'
                    slug: module.slug,
                    title: module.title,
                    keywords: module.keywords,
                    icon: <i className={module.icon}></i>,
                    title_url: module.title_url,
                    default: module.default,
                    doc_url: module.doc_url,
                    is_pro: module.is_pro,
                    description: module.description,
                    is_active: module.is_activate !== undefined ? module.is_activate : true, // Check if is_activate is set
                    demo_url: module.demo_url !== undefined ? module.demo_url : module.doc_url
                });
            }
        }

        return modules;

    }

    return (
        <div className='rounded-lg bg-white w-full mb-4'>
            <div className='flex flex-col md:flex-row md:items-center md:justify-between p-4'
            style={{
                paddingBottom: '0'
            }}>
                <p className='m-0 text-sm font-semibold text-text-primary mb-2 md:mb-0'>{__("Elements", "ultimate_vc")}</p>
                <div className='flex flex-col md:flex-row items-center gap-y-2 md:gap-x-2 md:mr-7 relative'>
                    <SearchIcon
                        className="absolute top-1/2 transform -translate-y-1/2 text-gray-400"
                        style={{
                            backgroundColor: '#F9FAFB',
                            left: '2%',
                            width: '18px',
                            height: '18px'
                        }} />
                    <input
                        type="search"
                        placeholder={__('Search elements...', 'ultimate_vc')}
                        className="mr-2 pl-10 w-full md:w-auto"
                        style={{
                            height: '40px',
                            borderColor: '#e0e0e0',
                            outline: 'none',
                            boxShadow: 'none',
                            backgroundColor: '#F9FAFB',
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#0017E1'}
                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                        onChange={handleSearchChange}
                    />
                    <div className="flex flex-row gap-2 w-full md:w-auto">
                        <Button
                            icon={loadingActivate ? <LoaderCircle className="animate-spin" /> : null}
                            iconPosition="left"
                            variant="outline"
                            className="uavc-bulk-action-button"
                            onClick={handleActivateAll}
                            disabled={loadingActivate || loadingDeactivate || !!searchTerm}
                        >
                            {loadingActivate ? __('Activating...', 'ultimate_vc') : __('Activate All', 'ultimate_vc')}
                        </Button>

                        <Button
                            icon={loadingDeactivate ? <LoaderCircle className="animate-spin" /> : null}
                            iconPosition="left"
                            variant="outline"
                            onClick={handleDeactivateAll}
                            className="uavc-bulk-action-button"
                            disabled={loadingActivate || loadingDeactivate || !!searchTerm}
                        >
                            {loadingDeactivate ? __('Deactivating...', 'ultimate_vc') : __('Deactivate All', 'ultimate_vc')}
                        </Button>
                    </div>
                </div>
            </div>
            <div className='flex bg-black flex-col rounded-lg p-4'>
                {loading ? (
                    <Container
                        align="stretch"
                        className="p-2 gap-1.5 grid grid-cols-2 md:grid-cols-4"
                        style={{
                            backgroundColor: "#F9FAFB"
                        }}
                        containerType="grid"
                        gap=""
                        justify="start"
                    >
                        {[...Array(40)].map((_, index) => (
                            <Container.Item
                                key={index}
                                alignSelf="auto"
                                className="text-wrap rounded-md shadow-container-item bg-background-primary p-6 space-y-2"
                            >
                                <Skeleton className='w-12 h-2 rounded-md' />
                                <Skeleton className='w-16 h-2 rounded-md' />
                                <Skeleton className='w-12 h-2 rounded-md' />
                            </Container.Item>
                        ))}
                    </Container>
                ) : (
                    <Container
                        align="stretch"
                        className="p-1 gap-1.5 grid-cols-2 md:grid-cols-3"
                        containerType="grid"
                        gap=""
                        justify="start"
                        style={{
                            backgroundColor: '#F9FAFB'
                        }}
                    >
                        {filteredModules?.map((module) => (
                            <Container.Item
                                key={`${module.id}-${updateCounter}`}
                                alignSelf="auto"
                                className="text-wrap rounded-md shadow-container-item bg-background-primary p-4"
                            >
                                <ModuleItem 
                                    module={module} 
                                    showTooltip={showTooltip} 
                                    updateCounter={updateCounter} 
                                />
                            </Container.Item>
                        ))}
                    </Container>
                )}
            </div>
        </div>
    );
};

export default ElementModules;
